import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { UserPlus, Copy, Check, Loader2, Trash2, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function InviteColleague() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "juriste", message: "" });
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [copiedToken, setCopiedToken] = useState(null);

  const refresh = () => {
    api.get("/invitations").then(({ data }) => setItems(data.items || []));
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/invitations", form);
      toast.success(`Invitation créée pour ${form.email}`);
      // copy URL to clipboard immediately
      await navigator.clipboard.writeText(data.invite_url);
      setCopiedToken(data.token);
      setTimeout(() => setCopiedToken(null), 3000);
      setForm({ email: "", role: form.role, message: "" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur création");
    } finally { setBusy(false); }
  };

  const copy = async (url, token) => {
    await navigator.clipboard.writeText(url);
    setCopiedToken(token);
    toast.success("Lien copié dans le presse-papiers");
    setTimeout(() => setCopiedToken(null), 3000);
  };

  const remove = async (token) => {
    if (!window.confirm("Supprimer cette invitation ?")) return;
    await api.delete(`/invitations/${token}`);
    refresh();
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        variant="outline"
        className="rounded-sm gap-2"
        data-testid="invite-open-btn"
      >
        <UserPlus size={14} />
        Inviter un collègue
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <UserPlus size={18} style={{ color: "#D4A017" }} />
              Inviter un collègue
            </DialogTitle>
            <DialogDescription>
              Générez un lien magique valable 7 jours. Partagez-le par email, Signal ou WhatsApp.
              Le rôle est pré-sélectionné à l'inscription.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="inv-email" className="text-xs uppercase tracking-wider opacity-70">
                Email du collègue
              </Label>
              <Input
                id="inv-email"
                type="email" required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="parlementaire@parlement.gouv.xx"
                data-testid="invite-email"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider opacity-70">Rôle pré-rempli</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="juriste">Juriste / Avocat</SelectItem>
                  <SelectItem value="parlementaire">Parlementaire</SelectItem>
                  <SelectItem value="gouvernement">Gouvernement</SelectItem>
                  <SelectItem value="ong">ONG / Société civile</SelectItem>
                  <SelectItem value="chercheur">Chercheur</SelectItem>
                  <SelectItem value="citoyen">Citoyen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider opacity-70">Message (optionnel)</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Un mot pour expliquer pourquoi tu l'invites…"
                rows={2} maxLength={500}
                className="rounded-sm mt-1 text-xs"
                data-testid="invite-message"
              />
            </div>

            <Button
              type="submit"
              disabled={busy || !form.email}
              className="w-full rounded-sm font-semibold"
              style={{ background: "#1B4332", color: "white" }}
              data-testid="invite-submit"
            >
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <UserPlus size={14} className="mr-2" />}
              Générer le lien d'invitation (le lien sera copié)
            </Button>
          </form>

          {items.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider opacity-70 mb-2">
                Mes invitations ({items.length})
              </div>
              <div className="max-h-[280px] overflow-y-auto space-y-2">
                {items.map((it) => {
                  const used = it.used;
                  const expired = it.expired;
                  const color = used ? "#27AE60" : expired ? "#E67E22" : "#1A3C5E";
                  const Icon = used ? CheckCircle2 : expired ? AlertTriangle : Clock;
                  const label = used ? "Acceptée" : expired ? "Expirée" : "En attente";
                  return (
                    <div
                      key={it.token}
                      className="border rounded-sm p-3 flex items-start gap-3"
                      style={{ borderColor: "hsl(var(--border))" }}
                      data-testid={`invite-item-${it.token.slice(0, 8)}`}
                    >
                      <Icon size={14} style={{ color, marginTop: 2, flexShrink: 0 }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{it.email}</div>
                        <div className="text-[10px] opacity-60 font-mono">
                          <span style={{ color }}>{label}</span> · {it.role} · créée le {new Date(it.created_at).toLocaleDateString("fr-FR")}
                        </div>
                      </div>
                      {!used && !expired && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => copy(it.invite_url, it.token)}
                          className="rounded-sm h-7 text-xs"
                          data-testid={`invite-copy-${it.token.slice(0, 8)}`}
                        >
                          {copiedToken === it.token ? <Check size={12} /> : <Copy size={12} />}
                        </Button>
                      )}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => remove(it.token)}
                        className="rounded-sm h-7"
                        data-testid={`invite-delete-${it.token.slice(0, 8)}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
